import type { ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';
import { Modal } from './Modal';
import { cn } from '../lib/cn';

type Shortcut = {
  /** Pieces rendered as separate <kbd>'s, joined with `+` or `→` */
  keys: string[];
  /** Connector between keys: '+' for chord, '→' for sequence */
  connector?: '+' | 'then';
  description: string;
};

type Group = {
  title: string;
  items: Shortcut[];
};

const GROUPS: Group[] = [
  {
    title: 'Navigation',
    items: [
      { keys: ['Cmd', 'K'], connector: '+', description: 'Open command palette' },
      { keys: ['/'], description: 'Focus search' },
      { keys: ['g', 'd'], connector: 'then', description: 'Go to dashboard' },
      { keys: ['g', 'b'], connector: 'then', description: 'Go to billing' },
      { keys: ['g', 's'], connector: 'then', description: 'Go to settings' },
    ],
  },
  {
    title: 'Actions',
    items: [
      { keys: ['n'], description: 'New (in any list page)' },
      { keys: ['e'], description: 'Edit' },
      { keys: ['Enter'], description: 'Save' },
      { keys: ['Esc'], description: 'Close' },
      { keys: ['r'], description: 'Refresh' },
    ],
  },
  {
    title: 'Help',
    items: [
      { keys: ['?'], description: 'Show this dialog' },
      { keys: ['h'], description: 'Open help center' },
    ],
  },
];

/** Global keyboard-shortcuts cheat sheet, controllable from the parent. */
export function KeyboardShortcutsModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Modal open={open} onClose={onClose} title="Keyboard shortcuts" size="lg">
      <div className="space-y-6">
        {GROUPS.map((group) => (
          <section key={group.title}>
            <h3 className="text-xs uppercase tracking-wider font-semibold text-zinc-500 dark:text-zinc-400 mb-3">
              {group.title}
            </h3>
            <ul className="divide-y divide-zinc-200 dark:divide-zinc-700 rounded-md border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-950 overflow-hidden">
              {group.items.map((item, idx) => (
                <li
                  key={`${group.title}-${idx}`}
                  className="flex items-center gap-4 px-4 py-2.5"
                >
                  <KeyCluster keys={item.keys} connector={item.connector} />
                  <span className="text-sm text-zinc-700 dark:text-zinc-200">
                    {item.description}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}

        <div className="pt-4 border-t border-zinc-200 dark:border-zinc-700 text-xs text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5">
          <span>View all shortcuts in</span>
          <a
            href="/help"
            onClick={onClose}
            className="inline-flex items-center gap-1 font-medium text-brand dark:text-brand-dm hover:underline"
          >
            Help &rarr; Keyboard shortcuts
            <ArrowRight className="w-3 h-3" />
          </a>
        </div>
      </div>
    </Modal>
  );
}

function KeyCluster({
  keys,
  connector = '+',
}: {
  keys: string[];
  connector?: '+' | 'then';
}) {
  const sep: ReactNode =
    connector === 'then' ? (
      <span className="text-xs text-zinc-400 dark:text-zinc-500 font-medium">then</span>
    ) : (
      <span className="text-xs text-zinc-400 dark:text-zinc-500">+</span>
    );

  return (
    <span className="inline-flex items-center gap-1.5 flex-shrink-0 w-32">
      {keys.map((k, i) => (
        <span key={`${k}-${i}`} className="inline-flex items-center gap-1.5">
          {i > 0 && sep}
          <Kbd>{k}</Kbd>
        </span>
      ))}
    </span>
  );
}

function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd
      className={cn(
        'inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded',
        'border border-zinc-300 dark:border-zinc-600',
        'bg-zinc-50 dark:bg-zinc-800',
        'text-[11px] font-medium font-mono text-zinc-700 dark:text-zinc-200',
        'shadow-[inset_0_-1px_0_rgba(0,0,0,0.08)] dark:shadow-[inset_0_-1px_0_rgba(0,0,0,0.4)]',
      )}
    >
      {children}
    </kbd>
  );
}
